'use client';

import React, { useState, useMemo, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { fetchColleges, College } from '@/lib/services/collegeService';
import { Search, X, Star, MapPin, CheckCircle2, XCircle, ChevronRight, GitCompare, Trophy, TrendingUp, DollarSign, BookOpen, Building2, Users, Award, Zap, BarChart3, GraduationCap, Home, BadgeCheck } from 'lucide-react';
import Link from 'next/link';

const MAX_COMPARE = 3;

type TabKey = 'overview' | 'academics' | 'placements' | 'fees' | 'facilities';

const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: 'Overview', icon: <BarChart3 size={15} /> },
  { key: 'academics', label: 'Academics', icon: <BookOpen size={15} /> },
  { key: 'placements', label: 'Placements', icon: <TrendingUp size={15} /> },
  { key: 'fees', label: 'Fees & Aid', icon: <DollarSign size={15} /> },
  { key: 'facilities', label: 'Facilities', icon: <Building2 size={15} /> },
];

const COLORS = ['#CC0000', '#1d4ed8', '#059669'];
const LIGHT_COLORS = ['bg-red-50 border-red-200', 'bg-blue-50 border-blue-200', 'bg-emerald-50 border-emerald-200'];
const TEXT_COLORS = ['text-red-600', 'text-blue-600', 'text-emerald-600'];
const BADGE_COLORS = ['bg-red-100 text-red-700', 'bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700'];

function formatCurrency(n: number) {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  return `₹${(n / 1000).toFixed(0)}K`;
}

function ScoreBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
      <div
        className="h-2 rounded-full transition-all duration-700"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
}

function StatCard({
  label, values, max, format, higherBetter = true, icon
}: {
  label: string;
  values: (number | null)[];
  max: number;
  format: (v: number) => string;
  higherBetter?: boolean;
  icon: React.ReactNode;
}) {
  const bestIdx = useMemo(() => {
    const valid = values.map((v, i) => ({ v: v ?? 0, i })).filter(x => x.v > 0);
    if (valid.length < 2) return -1;
    return higherBetter
      ? valid.reduce((a, b) => (a.v > b.v ? a : b)).i
      : valid.reduce((a, b) => (a.v < b.v ? a : b)).i;
  }, [values, higherBetter]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-gray-50 rounded-lg text-gray-500">{icon}</div>
        <span className="text-sm font-semibold text-gray-600">{label}</span>
        {bestIdx >= 0 && (
          <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${BADGE_COLORS[bestIdx]}`}>
            Best
          </span>
        )}
      </div>
      <div className="space-y-3">
        {values.map((v, i) => (
          <div key={i}>
            <div className="flex justify-between items-center mb-1">
              <span className={`text-sm font-bold ${i === bestIdx ? TEXT_COLORS[i] : 'text-gray-700'}`}>
                {v != null ? format(v) : 'N/A'}
              </span>
            </div>
            <ScoreBar value={v ?? 0} max={max} color={COLORS[i]} />
          </div>
        ))}
      </div>
    </div>
  );
}

function CompareCollegesContent() {
  const searchParams = useSearchParams();
  const [colleges, setColleges] = useState<College[]>([]);
  const [loadingColleges, setLoadingColleges] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  useEffect(() => {
    fetchColleges().then((data) => {
      setColleges(data);
      setLoadingColleges(false);
    });
  }, []);

  // Pre-select colleges from URL query params
  useEffect(() => {
    if (colleges.length === 0) return;
    const idsParam = searchParams.get('ids');
    if (idsParam) {
      const ids = idsParam.split(',').filter(Boolean).slice(0, MAX_COMPARE);
      const validIds = ids.filter((id) => colleges.some((c) => c.id === id));
      if (validIds.length > 0) {
        setSelectedIds(validIds);
      }
    }
  }, [searchParams, colleges]);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return colleges.filter(
      (c) =>
        !selectedIds.includes(c.id) &&
        (c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.shortName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.location.toLowerCase().includes(searchQuery.toLowerCase()))
    ).slice(0, 8);
  }, [searchQuery, selectedIds]);

  const selectedColleges = useMemo(
    () => colleges.filter((c) => selectedIds.includes(c.id)),
    [selectedIds]
  );

  const addCollege = (id: string) => {
    if (selectedIds.length < MAX_COMPARE) {
      setSelectedIds([...selectedIds, id]);
      setSearchQuery('');
      setShowSearch(false);
    }
  };

  const removeCollege = (id: string) => {
    setSelectedIds(selectedIds.filter((s) => s !== id));
  };

  const getBestIdx = (key: keyof College, higherBetter = true) => {
    if (selectedColleges.length < 2) return -1;
    const vals = selectedColleges.map((c) => Number(c[key] ?? 0));
    const valid = vals.filter((v) => v > 0);
    if (valid.length < 2) return -1;
    const target = higherBetter ? Math.max(...vals) : Math.min(...vals.filter(v => v > 0));
    return vals.indexOf(target);
  };

  const canCompare = selectedColleges.length >= 2;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <div className="bg-secondary text-white py-10 px-4 lg:px-8 xl:px-10 2xl:px-16">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-2 text-slate-300 text-sm mb-3">
              <Link href="/home" className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span>Compare Colleges</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div>
                <h1 className="text-3xl font-extrabold text-white mb-2">Compare Colleges</h1>
                <p className="text-slate-300 text-sm max-w-xl">
                  Select up to 3 colleges and compare them side-by-side on fees, rankings, placements, and more.
                </p>
              </div>
              {selectedColleges.length > 0 && (
                <button
                  onClick={() => setSelectedIds([])}
                  className="ml-auto flex items-center gap-2 text-sm text-slate-300 hover:text-white border border-slate-500 hover:border-white px-4 py-2 rounded-xl transition-all"
                >
                  <X size={14} /> Clear All
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">

          {/* College selector slots */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {Array.from({ length: MAX_COMPARE }, (_, i) => {
              const college = selectedColleges[i];
              const colorIdx = i;
              return (
                <div key={`slot-${i}`} className="relative">
                  {college ? (
                    <div className={`bg-white rounded-2xl border-2 p-4 relative shadow-sm ${LIGHT_COLORS[colorIdx]}`}>
                      <button
                        onClick={() => removeCollege(college.id)}
                        className="absolute top-3 right-3 p-1.5 rounded-full bg-white/80 hover:bg-red-100 hover:text-red-600 transition-colors shadow-sm z-10"
                      >
                        <X size={13} />
                      </button>
                      <div className="relative h-28 rounded-xl overflow-hidden mb-3">
                        <img src={college.image} alt={`${college.name} campus`} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-2 left-2 flex gap-1.5">
                          <span className="text-xs bg-white/90 text-gray-700 font-bold px-2 py-0.5 rounded-full">
                            {college.ownership}
                          </span>
                          {college.nirfRank && (
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white`} style={{ backgroundColor: COLORS[colorIdx] }}>
                              NIRF #{college.nirfRank}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={`w-6 h-1.5 rounded-full mb-2`} style={{ backgroundColor: COLORS[colorIdx] }} />
                      <h3 className="font-bold text-sm text-gray-900 line-clamp-2 mb-1.5">{college.name}</h3>
                      <div className="flex items-center gap-1 text-gray-500 mb-2">
                        <MapPin size={11} />
                        <span className="text-xs">{college.location}, {college.state}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <Star size={11} className="text-amber-400 fill-amber-400" />
                          <span className="text-xs font-bold text-gray-800">{college.rating}</span>
                          <span className="text-xs text-gray-400">({college.reviewCount.toLocaleString()})</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          {college.accreditation}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowSearch(true)}
                      className="w-full h-full min-h-[180px] bg-white rounded-2xl border-2 border-dashed border-gray-200 hover:border-primary hover:bg-red-50/40 transition-all flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-primary shadow-sm"
                    >
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <GitCompare size={22} />
                      </div>
                      <span className="text-sm font-semibold">Add College {i + 1}</span>
                      <span className="text-xs text-gray-400">Click to search</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Search overlay */}
          {showSearch && selectedIds.length < MAX_COMPARE && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-20 px-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                  <Search size={16} className="text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search college by name or city..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                    className="flex-1 outline-none text-sm text-gray-800 bg-transparent placeholder:text-gray-400"
                  />
                  <button onClick={() => { setShowSearch(false); setSearchQuery(''); }} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                    <X size={16} />
                  </button>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {searchResults.length > 0 ? (
                    searchResults.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => addCollege(c.id)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-0"
                      >
                        <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
                          <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-900 truncate">{c.name}</p>
                          <p className="text-xs text-gray-500">{c.location}, {c.state}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {c.nirfRank && (
                            <span className="text-xs bg-red-50 text-primary font-bold px-2 py-0.5 rounded-full">NIRF #{c.nirfRank}</span>
                          )}
                          <span className="text-xs text-gray-400">{c.ownership}</span>
                        </div>
                      </button>
                    ))
                  ) : searchQuery.length > 0 ? (
                    <p className="text-center text-gray-400 text-sm py-8">No colleges found</p>
                  ) : (
                    <div className="p-4 space-y-1">
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-3">Popular Colleges</p>
                      {colleges.filter((c) => !selectedIds.includes(c.id)).slice(0, 6).map((c) => (
                        <button
                          key={c.id}
                          onClick={() => addCollege(c.id)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left"
                        >
                          <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0">
                            <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                          </div>
                          <p className="font-semibold text-sm text-gray-800 flex-1 truncate">{c.shortName}</p>
                          {c.nirfRank && (
                            <span className="text-xs text-gray-400">NIRF #{c.nirfRank}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Main comparison content */}
          {canCompare ? (
            <div className="space-y-6">

              {/* Quick stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Avg Package', key: 'placementAvgPackage' as keyof College, icon: <TrendingUp size={16} />, format: formatCurrency, higherBetter: true, max: Math.max(...selectedColleges.map(c => c.placementAvgPackage)) * 1.2 },
                  { label: 'NIRF Rank', key: 'nirfRank' as keyof College, icon: <Trophy size={16} />, format: (v: number) => `#${v}`, higherBetter: false, max: Math.max(...selectedColleges.map(c => c.nirfRank ?? 999)) },
                  { label: 'Rating', key: 'rating' as keyof College, icon: <Star size={16} />, format: (v: number) => `${v}/10`, higherBetter: true, max: 10 },
                  { label: 'Total Fees', key: 'totalFees' as keyof College, icon: <DollarSign size={16} />, format: formatCurrency, higherBetter: false, max: Math.max(...selectedColleges.map(c => c.totalFees)) * 1.2 },
                ].map((stat) => (
                  <StatCard
                    key={stat.key}
                    label={stat.label}
                    values={selectedColleges.map(c => Number(c[stat.key] ?? 0))}
                    max={stat.max}
                    format={stat.format}
                    higherBetter={stat.higherBetter}
                    icon={stat.icon}
                  />
                ))}
              </div>

              {/* Tabs */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex overflow-x-auto border-b border-gray-100">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold whitespace-nowrap transition-all border-b-2 ${
                        activeTab === tab.key
                          ? 'border-primary text-primary bg-red-50/50' :'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="overflow-x-auto">
                  {/* Column headers */}
                  <div className="grid border-b border-gray-100" style={{ gridTemplateColumns: `200px repeat(${selectedColleges.length}, 1fr)` }}>
                    <div className="px-5 py-4 bg-gray-50" />
                    {selectedColleges.map((c, i) => (
                      <div key={c.id} className="px-5 py-4 text-center bg-gray-50">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i] }} />
                          <span className="text-sm font-bold text-gray-800 truncate">{c.shortName}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Tab content */}
                  {activeTab === 'overview' && (
                    <CompareSection
                      rows={[
                        { label: 'NIRF Rank', icon: <Trophy size={14} />, render: (c, isBest) => renderBadge(c.nirfRank ? `#${c.nirfRank}` : 'N/A', isBest) },
                        { label: 'Accreditation', icon: <BadgeCheck size={14} />, render: (c) => <span className="text-sm font-semibold text-gray-700">{c.accreditation}</span> },
                        { label: 'Ownership', icon: <Building2 size={14} />, render: (c) => <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${c.ownership === 'Government' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>{c.ownership}</span> },
                        { label: 'Established', icon: <GraduationCap size={14} />, render: (c) => <span className="text-sm font-semibold text-gray-700">{c.established}</span> },
                        { label: 'Rating', icon: <Star size={14} />, render: (c, isBest) => (
                          <div className="flex items-center justify-center gap-1.5">
                            <Star size={13} className="text-amber-400 fill-amber-400" />
                            <span className={`text-sm font-bold ${isBest ? 'text-green-600' : 'text-gray-800'}`}>{c.rating}</span>
                            <span className="text-xs text-gray-400">({c.reviewCount.toLocaleString()})</span>
                          </div>
                        )},
                        { label: 'Location', icon: <MapPin size={14} />, render: (c) => <span className="text-sm text-gray-600">{c.location}, {c.state}</span> },
                        { label: 'Streams', icon: <BookOpen size={14} />, render: (c) => (
                          <div className="flex flex-wrap justify-center gap-1">
                            {c.streams.slice(0, 3).map(s => (
                              <span key={s} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{s}</span>
                            ))}
                            {c.streams.length > 3 && <span className="text-xs text-gray-400">+{c.streams.length - 3}</span>}
                          </div>
                        )},
                      ]}
                      colleges={selectedColleges}
                      bestIdxFn={(row) => {
                        if (row.label === 'NIRF Rank') return getBestIdx('nirfRank', false);
                        if (row.label === 'Rating') return getBestIdx('rating', true);
                        return -1;
                      }}
                    />
                  )}

                  {activeTab === 'academics' && (
                    <CompareSection
                      rows={[
                        { label: 'Cutoff Exam', icon: <BookOpen size={14} />, render: (c) => <span className="text-sm font-semibold text-gray-700">{c.cutoffExam}</span> },
                        { label: 'Cutoff Score', icon: <Zap size={14} />, render: (c, isBest) => renderBadge(`${c.cutoffScore} ${c.cutoffUnit}`, isBest) },
                        { label: 'Total Courses', icon: <GraduationCap size={14} />, render: (c, isBest) => renderBadge(String(c.courses.length), isBest) },
                        { label: 'UG Courses', icon: <BookOpen size={14} />, render: (c, isBest) => renderBadge(String(c.courses.filter(x => x.level === 'UG').length), isBest) },
                        { label: 'PG Courses', icon: <BookOpen size={14} />, render: (c, isBest) => renderBadge(String(c.courses.filter(x => x.level === 'PG').length), isBest) },
                        { label: 'Total Seats', icon: <Users size={14} />, render: (c, isBest) => renderBadge(String(c.courses.reduce((a, b) => a + b.totalSeats, 0)), isBest) },
                        { label: 'Accreditation', icon: <Award size={14} />, render: (c) => <span className="text-sm font-semibold text-gray-700">{c.accreditation}</span> },
                      ]}
                      colleges={selectedColleges}
                      bestIdxFn={(row) => {
                        if (row.label === 'Cutoff Score') return getBestIdx('cutoffScore', false);
                        if (row.label === 'Total Courses') return getBestIdx('cutoffScore', true);
                        return -1;
                      }}
                    />
                  )}

                  {activeTab === 'placements' && (
                    <CompareSection
                      rows={[
                        { label: 'Avg Package', icon: <TrendingUp size={14} />, render: (c, isBest) => renderBadge(formatCurrency(c.placementAvgPackage), isBest, isBest ? 'text-green-600 bg-green-50' : '') },
                        { label: 'Highest Package', icon: <Trophy size={14} />, render: (c, isBest) => renderBadge(formatCurrency(c.placementHighestPackage), isBest, isBest ? 'text-green-600 bg-green-50' : '') },
                        { label: 'Package Bar', icon: <BarChart3 size={14} />, render: (c, _isBest, idx) => {
                          const maxPkg = Math.max(...selectedColleges.map(x => x.placementAvgPackage));
                          return (
                            <div className="w-full px-2">
                              <ScoreBar value={c.placementAvgPackage} max={maxPkg * 1.1} color={COLORS[idx ?? 0]} />
                            </div>
                          );
                        }},
                        { label: 'Top Recruiter Sectors', icon: <Building2 size={14} />, render: (c) => (
                          <div className="flex flex-wrap justify-center gap-1">
                            {c.streams.slice(0, 2).map(s => (
                              <span key={s} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{s}</span>
                            ))}
                          </div>
                        )},
                      ]}
                      colleges={selectedColleges}
                      bestIdxFn={(row) => {
                        if (row.label === 'Avg Package') return getBestIdx('placementAvgPackage', true);
                        if (row.label === 'Highest Package') return getBestIdx('placementHighestPackage', true);
                        return -1;
                      }}
                    />
                  )}

                  {activeTab === 'fees' && (
                    <CompareSection
                      rows={[
                        { label: 'Total Fees', icon: <DollarSign size={14} />, render: (c, isBest) => renderBadge(formatCurrency(c.totalFees), isBest, isBest ? 'text-green-600 bg-green-50' : '') },
                        { label: 'Fees Per Year', icon: <DollarSign size={14} />, render: (c, isBest) => renderBadge(formatCurrency(c.feesPerYear), isBest, isBest ? 'text-green-600 bg-green-50' : '') },
                        { label: 'Fees Bar', icon: <BarChart3 size={14} />, render: (c, _isBest, idx) => {
                          const maxFee = Math.max(...selectedColleges.map(x => x.totalFees));
                          return (
                            <div className="w-full px-2">
                              <ScoreBar value={c.totalFees} max={maxFee * 1.1} color={COLORS[idx ?? 0]} />
                            </div>
                          );
                        }},
                        { label: 'Scholarships', icon: <Award size={14} />, render: (c) => renderBoolBadge(c.scholarships) },
                        { label: 'Cheapest Option', icon: <Zap size={14} />, render: (c, isBest) => (
                          isBest
                            ? <span className="flex items-center gap-1 text-green-600 text-xs font-bold"><CheckCircle2 size={13} /> Best Value</span>
                            : <span className="text-xs text-gray-400">—</span>
                        )},
                      ]}
                      colleges={selectedColleges}
                      bestIdxFn={(row) => {
                        if (row.label === 'Total Fees' || row.label === 'Fees Per Year' || row.label === 'Cheapest Option') return getBestIdx('totalFees', false);
                        return -1;
                      }}
                    />
                  )}

                  {activeTab === 'facilities' && (
                    <CompareSection
                      rows={[
                        { label: 'Hostel', icon: <Home size={14} />, render: (c) => renderBoolBadge(c.hostel) },
                        { label: 'Scholarships', icon: <Award size={14} />, render: (c) => renderBoolBadge(c.scholarships) },
                        { label: 'Campus Type', icon: <Building2 size={14} />, render: (c) => <span className="text-sm font-semibold text-gray-700">{c.ownership}</span> },
                        { label: 'Streams Offered', icon: <BookOpen size={14} />, render: (c) => (
                          <div className="flex flex-wrap justify-center gap-1">
                            {c.streams.map(s => (
                              <span key={s} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{s}</span>
                            ))}
                          </div>
                        )},
                      ]}
                      colleges={selectedColleges}
                      bestIdxFn={() => -1}
                    />
                  )}
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 bg-white rounded-xl border border-gray-100 px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={13} className="text-green-500" />
                  <span>Green = Best value among selected colleges</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="flex gap-1">
                    {selectedColleges.map((c, i) => (
                      <div key={c.id} className="flex items-center gap-1">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                        <span>{c.shortName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {selectedColleges.map((c, i) => (
                  <Link
                    key={c.id}
                    href={`/college/${c.id}`}
                    className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 shadow-sm"
                    style={{ backgroundColor: COLORS[i] }}
                  >
                    <GraduationCap size={15} />
                    View {c.shortName}
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
                <GitCompare size={36} className="text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Add at least 2 colleges to compare</h3>
              <p className="text-gray-500 text-sm max-w-sm mx-auto mb-6">
                Click the &quot;Add College&quot; slots above to select colleges for a detailed side-by-side comparison.
              </p>
              <button
                onClick={() => setShowSearch(true)}
                className="btn-primary"
              >
                Search & Add College
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default function CompareCollegesPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>}>
      <CompareCollegesContent />
    </Suspense>
  );
}

// Helper render functions
function renderBadge(value: string, isBest: boolean, extraClass = '') {
  return (
    <span className={`text-sm font-bold px-3 py-1 rounded-full ${isBest ? 'bg-green-100 text-green-700' : 'text-gray-800'} ${extraClass}`}>
      {value}
    </span>
  );
}

function renderBoolBadge(value: boolean) {
  return value ? (
    <span className="flex items-center justify-center gap-1.5 text-green-600 text-xs font-bold bg-green-50 px-3 py-1 rounded-full">
      <CheckCircle2 size={13} /> Available
    </span>
  ) : (
    <span className="flex items-center justify-center gap-1.5 text-red-500 text-xs font-bold bg-red-50 px-3 py-1 rounded-full">
      <XCircle size={13} /> Not Available
    </span>
  );
}

// CompareSection component
interface RowDef {
  label: string;
  icon: React.ReactNode;
  render: (college: College, isBest: boolean, idx?: number) => React.ReactNode;
}

function CompareSection({
  rows, colleges, bestIdxFn
}: {
  rows: RowDef[];
  colleges: College[];
  bestIdxFn: (row: RowDef) => number;
}) {
  return (
    <div className="divide-y divide-gray-50">
      {rows.map((row, ri) => {
        const bestIdx = bestIdxFn(row);
        return (
          <div
            key={row.label}
            className={`grid items-center ${ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
            style={{ gridTemplateColumns: `200px repeat(${colleges.length}, 1fr)` }}
          >
            <div className="px-5 py-4 flex items-center gap-2 text-gray-500">
              {row.icon}
              <span className="text-sm font-semibold text-gray-600">{row.label}</span>
            </div>
            {colleges.map((c, i) => (
              <div key={c.id} className="px-5 py-4 flex items-center justify-center">
                {row.render(c, i === bestIdx, i)}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// Local getBestIdx helper used inside CompareSection bestIdxFn closures
function getBestIdx(key: keyof College, higherBetter: boolean) {
  return (colleges: College[]) => {
    if (colleges.length < 2) return -1;
    const vals = colleges.map((c) => Number(c[key] ?? 0));
    const valid = vals.filter((v) => v > 0);
    if (valid.length < 2) return -1;
    const target = higherBetter ? Math.max(...vals) : Math.min(...vals.filter(v => v > 0));
    return vals.indexOf(target);
  };
}
